// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useMemo, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../context/FirebaseConfig';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DeliveryDetailsModal from '../DeliveryDetails/DeliveryDetailsModal';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getClaimSettings, getDeliverySettings } from '../../shared_components/utils/FirebaseUtils';
import './IssuesTab.scss';

function IssuesTab({ supplier, supplierData, activeFilters = {}}) {
  const { t } = useTranslation();
  const { currentUser, logEventCustom, labelsets } = React.useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
  const [claimSettings, setClaimSettings] = useState(null);
  const [deliveriesSettings, setDeliveriesSettings] = useState(null);
  
  // Load claim settings
  useEffect(() => {
    getClaimSettings(firestore, currentUser.company.company).then(settings => {
      setClaimSettings(settings);
    });
    getDeliverySettings(firestore, currentUser.company.company).then(settings => {
      setDeliveriesSettings(settings);
    });
  }, []);
  
  // Function to get material display name
  function getMaterialDisplayName(materialKey) {
    return labelsets?.materials?.labels?.[materialKey]?.display_name || materialKey || 'Unknown';
  }
  
  // Extract deliveries with confirmed claims
  const deliveriesWithIssues = useMemo(() => {
    if (!supplierData?.deliveries) return [];
    
    return supplierData.deliveries
      .filter(delivery => {
        // Filter for confirmed claims
        return delivery.claim_data && delivery.claim_data.claim_status === 'confirmed';
      })
      .sort((a, b) => {
        // Sort by date, newest first
        const dateA = a.date ? new Date(a.date.toDate()).getTime() : 0;
        const dateB = b.date ? new Date(b.date.toDate()).getTime() : 0;
        return dateB - dateA;
      });
  }, [supplierData?.deliveries]);
  
  // Handle opening the delivery details modal
  function handleOpenDeliveryDetails(deliveryId) {
    setSelectedDeliveryId(deliveryId);
    setShowModal(true);

    logEventCustom('select_content', {content_type: 'open_delivery_from_issues_tab', content_id: deliveryId});
  }
  
  // Handle closing the delivery details modal
  function handleCloseModal() {
    setShowModal(false);
  }
  
  // Format delivery date
  function formatDeliveryDate(timestamp) {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'dd.MM.yyyy');
  }
  
  // Get display name for issue
  function getIssueDisplayName(issueCode) {
    return claimSettings?.delivery_issues?.[issueCode]?.display_name || issueCode;
  }
  
  // Render each issue card
  function renderIssueCard(delivery) {
    // Get issues array or default to empty array
    const issues = delivery.claim_data?.delivery_issues || [];
    
    return (
      <div 
        key={delivery.id} 
        className="issue-card"
        onClick={() => handleOpenDeliveryDetails(delivery.id)}
      >
        <div className="issue-header">
          <div className="issue-icon">
            <WarningAmberIcon />
          </div>
          <div className="issue-title">
            {issues.length > 0 
              ? issues.map(issueCode => getIssueDisplayName(issueCode)).join(', ')
              : t('issue_reported')
            }
          </div>
        </div>
        <div className="issue-date">
          {t('delivery_date')}: {formatDeliveryDate(delivery.date)}
        </div>
        {delivery.base_labels?.materials && (
          <div className="issue-material">
            {t('property_material')}: {getMaterialDisplayName(delivery.base_labels.materials)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-issues animate-fade-in">
      <div className="tab-header">
        <h3>{t('issue_history')}</h3>
        {activeFilters?.material && activeFilters?.material !== 'all' && (
          <span className="material-filter-badge">
            {getMaterialDisplayName(activeFilters?.material)}
          </span>
        )}
        {activeFilters?.selectedClaimIssues && (
          <span className="material-filter-badge">
            {t('claim_issues_filter')}
          </span>
        )}
        {deliveriesWithIssues.length > 0 && (
          <div className="issues-count">
            <WarningAmberIcon fontSize="small" />
            <span>
              {deliveriesWithIssues.length} {deliveriesWithIssues.length === 1 
                ? t('issues_count') 
                : t('issues_count_plural')}
            </span>
          </div>
        )}
      </div>
      
      {deliveriesWithIssues.length === 0 ? (
        <div className="empty-message">{t('no_confirmed_issues')}</div>
      ) : (
        <div className="scroll-container">
          <div className="issues-container">
            {deliveriesWithIssues.map(delivery => renderIssueCard(delivery))}
          </div>
        </div>
      )}
      
      {/* Delivery Details Modal */}
      <DeliveryDetailsModal 
        show={showModal}
        onHide={handleCloseModal}
        deliveryId={selectedDeliveryId}
        deliveriesSettings={deliveriesSettings}
      />
    </div>
  );
}

export default IssuesTab; 